import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "public_interest_engagement";

const PublicInterestEngagement = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({
    starting_date: null,
    ending_date: null,
    role_name: "",
    entity_name: "",
    entity_nature: "",
    country: "",
  });
  const [recordId, setRecordId] = useState(null);
  const [showCustomNature, setShowCustomNature] = useState(false);

  const inputStyle = {
    padding: "10px",
    marginTop: "5px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    width: "100%",
    fontSize: "16px",
  };

  const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    const processed = (Array.isArray(data) ? data : [])
      .map((rec) => ({
        ...rec,
        starting_date: rec.starting_date
          ? new Date(rec.starting_date)
          : null,
        ending_date: rec.ending_date
          ? new Date(rec.ending_date)
          : null,
      }))
      .sort((a, b) => {
        if (!a.starting_date) return 1;
        if (!b.starting_date) return -1;

        return (
          b.starting_date.getTime() -
          a.starting_date.getTime()
        );
      });

    setRecords(processed);

  } catch (err) {
    toast.error(err.message);
  }
};

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleNatureChange = (e) => {
    const value = e.target.value;
    if (value === "Other") {
      setShowCustomNature(true);
      setFormData({ ...formData, entity_nature: "" });
    } else {
      setShowCustomNature(false);
      setFormData({ ...formData, entity_nature: value });
    }
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      starting_date: formData.starting_date
        ? `${formData.starting_date.getFullYear()}-${String(
            formData.starting_date.getMonth() + 1
          ).padStart(2, "0")}-01`
        : null,

      ending_date: formData.ending_date
        ? `${formData.ending_date.getFullYear()}-${String(
            formData.ending_date.getMonth() + 1
          ).padStart(2, "0")}-01`
        : null,
    };
    try {
      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save record");

      toast.success(
        recordId ? "Record updated successfully!" : "Record added successfully!"
      );
      setFormData({
        starting_date: null,
        ending_date: null,
        role_name: "",
        entity_name: "",
        entity_nature: "",
        country: "",
      });
      setRecordId(null);
      setShowCustomNature(false);
      fetchRecords();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (rec) => {
    setFormData({
      starting_date: rec.starting_date ? new Date(rec.starting_date) : null,
      ending_date: rec.ending_date ? new Date(rec.ending_date) : null,
      role_name: rec.role_name || "",
      entity_name: rec.entity_name || "",
      entity_nature: rec.entity_nature || "",
      country: rec.country || "",
    });
    setRecordId(rec.id);
    setShowCustomNature(rec.entity_nature === "Other");
    toast.info("Editing record...");
  };

  const handleDelete = (rec) => {
    const toastId = toast.info(
      <div>
        Delete this record?
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${rec.id}`,
                {
                  method: "DELETE",
                  credentials: "include",
                }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const formatDate = (date) => (date ? format(new Date(date), "MM/yyyy") : "");

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Public-Interest Institutions Engagement
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Dates Row */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>From (mm/yyyy)</label>
              <DatePicker
                selected={formData.starting_date}
                onChange={(date) =>
                  setFormData({ ...formData, starting_date: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>To (mm/yyyy)</label>
              <DatePicker
                selected={formData.ending_date}
                onChange={(date) =>
                  setFormData({ ...formData, ending_date: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Role Name */}
          <div style={{ marginTop: "10px" }}>
            <label style={{ fontWeight: "bold" }}>Name of the Role</label>
            <input
              type="text"
              name="role_name"
              value={formData.role_name}
              onChange={handleChange}
              required
              style={inputStyle}
            />
          </div>

          {/* Entity Name */}
          <div style={{ marginTop: "10px" }}>
            <label style={{ fontWeight: "bold" }}>
              Name of the Entity/Organization
            </label>
            <input
              type="text"
              name="entity_name"
              value={formData.entity_name}
              onChange={handleChange}
              required
              style={inputStyle}
            />
          </div>

          {/* Entity Nature */}
          <div style={{ marginTop: "10px" }}>
            <label style={{ fontWeight: "bold" }}>
              Nature of the Entity/Organization
            </label>
            <select
              name="entity_nature"
              value={showCustomNature ? "Other" : formData.entity_nature}
              onChange={handleNatureChange}
              required
              style={inputStyle}
            >
              <option value="">Select</option>
              <option value="Regulators">Regulators</option>
              <option value="Public Agencies">Public Agencies</option>
              <option value="NGOs">NGOs</option>
              <option value="Social Enterprise">Social Enterprise</option>
              <option value="Other">Other</option>
            </select>
            {showCustomNature && (
              <input
                type="text"
                placeholder="Enter entity nature"
                value={formData.entity_nature}
                onChange={(e) =>
                  setFormData({ ...formData, entity_nature: e.target.value })
                }
                style={{ ...inputStyle, marginTop: "5px" }}
                required
              />
            )}
          </div>

          {/* Country */}
          <div style={{ marginTop: "10px" }}>
            <label style={{ fontWeight: "bold" }}>Country</label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              style={inputStyle}
            />
          </div>

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {/* Records Table */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Records</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "From",
                    "To",
                    "Role",
                    "Entity",
                    "Nature",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatDate(rec.starting_date)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatDate(rec.ending_date)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.role_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.entity_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.entity_nature}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        gap: "5px",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicInterestEngagement;
